import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import BottomSheetModal from "./BottomSheetModal";
import { Divider, NumberedSteps } from "./SheetPrimitives";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";

import arrowRightIconAuth from "../../assets/images/authentication/arrow-right.svg";

// Stripe
import { loadStripe } from "@stripe/stripe-js";

// API
import { post } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage, isNetworkErrorHandledByModal } from "../../utils/errorHandler";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* -------------------------------------------------------------------------
 * ConnectAccountModal — prompts the user to link a payment account for
 * automatic cashback tracking. Built on the shared BottomSheetModal shell.
 * ---------------------------------------------------------------------- */

const DEFAULT_STEPS = [
  "Click on Connect Account Securely.",
  "You'll be redirected to Stripe's Secure account connection process.",
  "Enter your payment account details and complete the verification process.",
  "Once your account is successfully connected, you'll receive a confirmation.",
  "Use your connected account when dining at partner restaurants.",
  "Eligible purchases will automatically earn 5% back as Points within 24 hrs.",
  "You can redeem your earned points for vouchers that can be used at any partner restaurants."
];

const ConnectAccountModal = ({
  title,
  description,
  cashbackPercent,
  steps,
  isOpen,
  onClose,
  onConnect,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stripe, setStripe] = useState(null);

  // Load Stripe on component mount
  useEffect(() => {
    const stripeKey = process.env.REACT_APP_STRIPE_KEY;
    if (stripeKey && navigator.onLine) {
      loadStripe(stripeKey)
        .then(setStripe)
        .catch((error) => {
          // Silently handle Stripe loading errors - don't show toast
          console.error('Failed to load Stripe:', error);
        });
    }
  }, []);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const handleConnect = async () => {
    if (!stripe) {
      setError("Stripe is not loaded. Please refresh the page.");
      toastr.error("Stripe is not loaded. Please refresh the page.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // Create Financial Connections session with timeout
      const response = await post(endpoints.financialConnectionsSession, {
        // permissions: ["payment_method", "transactions"],
      }, { timeout: 30000 }); // 30 second timeout
      
      if (response.data.success && response.data.data.client_secret) {
        // Open Stripe Financial Connections modal with timeout
        const stripePromise = stripe.collectFinancialConnectionsAccounts({
          clientSecret: response.data.data.client_secret,
        });

        // Add timeout for Stripe modal
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Connection timed out. Please try again.")), 60000); // 60 second timeout
        });

        const { error: stripeError } = await Promise.race([stripePromise, timeoutPromise]);

        if (stripeError) {
          const errorMessage = stripeError.message || "Failed to connect account.";
          setError(errorMessage);
          toastr.error(errorMessage);
        } else {
          // Success - account connected
          // Call the complete endpoint to finalize with timeout
          try {
            await post(endpoints.financialConnectionsSessionComplete, {
              session_id: response.data.data.session_id,
            }, { timeout: 30000 }); // 30 second timeout
            toastr.success("Account connected successfully!");
            onClose();
            if (onConnect) onConnect();
          } catch (completeError) {
            const errorMessage = extractErrorMessage(completeError);
            setError(errorMessage);
            toastr.error(errorMessage);
          }
        }
      } else {
        setError("Failed to create session. Please try again.");
        toastr.error("Failed to create session. Please try again.");
      }
    } catch (error) {
      // Don't show toastr if this is a network error handled by the modal
      if (isNetworkErrorHandledByModal(error)) {
        setError("Network error. Please check your internet connection.");
        return;
      }

      const errorMessage = extractErrorMessage(error);

      // Handle specific network errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setError("Connection timed out. Please check your network and try again.");
        toastr.error("Connection timed out. Please check your network and try again.");
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        setError("Network error encountered. Please check your internet connection and try again.");
        // Don't show toastr for network errors - the modal handles it
      } else {
        setError(errorMessage);
        toastr.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <BottomSheetModal isOpen={isOpen} onClose={onClose} labelledBy="cam-title">
      <div className="cam-wrap">
        <h2 className="mc-title mc-title--center" id="cam-title" style={{ marginTop: "var(--gap-lg)" }}>
          {title}
        </h2>
        <p className="mc-description mc-description--center">
          Connect your payment account securely.
          <br />
          You&rsquo;ll automatically earn {cashbackPercent}% back every time you
          dine at a partner restaurant.
        </p>

        <Divider />

        <p className="mc-section-label">How it works and why:</p>
        <NumberedSteps steps={steps} />

        <Divider />

        {error ? (
          <p className="auth-server-error" style={{ marginBottom: "var(--gap-sm)" }}>
            {error}
          </p>
        ) : null}

        <CustomButton
          type="button"
          variant="primary"
          iconPosition="right"
          icon={arrowRightIconAuth}
          className="w-100 cam-connect-btn"
          onClick={handleConnect}
          loading={loading}
        >
          Connect Account Securely
        </CustomButton>

        <button type="button" className="mc-btn-secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </button>

        <p className="mc-footnote">
          By connecting, you agree to OFL Rewards&rsquo; terms of service and
          privacy policy.
        </p>
      </div>
    </BottomSheetModal>
  );
};

ConnectAccountModal.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  cashbackPercent: PropTypes.number,
  steps: PropTypes.arrayOf(PropTypes.string),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConnect: PropTypes.func,
  onCancel: PropTypes.func,
};

ConnectAccountModal.defaultProps = {
  title: "Link Your Account",
  description: undefined,
  cashbackPercent: 5,
  steps: DEFAULT_STEPS,
  onConnect: () => {},
  onCancel: () => {},
};

export default ConnectAccountModal;
